/**
 * EmitirServicioAdicionalDialog (rc44) — Modal pa emitir factura tipo 'libre'
 * (Servicio adicional) desde el detail de un contrato.
 *
 * Diferencias vs GenerarFacturaDialog:
 *   - Monto custom (no toma precio del plan)
 *   - Concepto descriptivo obligatorio (ej. "Cambio de domicilio")
 *   - NO cuenta para el poller de cobranza (no causa suspensión)
 *   - Requiere permiso facturas.libre.create (solo admin)
 *
 * Al éxito navega al detalle de la factura recién creada para que el admin
 * pueda revisarla o imprimir recibo.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2, Info } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEmitirServicioAdicional } from "./api";
import { toISODate, formatMXN } from "@/lib/utils";
import type { ContratoDetail } from "@/features/contratos/schemas";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  contrato: ContratoDetail;
  clienteRazonSocial?: string;
};

export function EmitirServicioAdicionalDialog({
  open, onOpenChange, contrato, clienteRazonSocial,
}: Props) {
  const navigate = useNavigate();
  const mut = useEmitirServicioAdicional();

  const [concepto, setConcepto] = useState("");
  const [monto, setMonto] = useState("");
  const [fechaVencimiento, setFechaVencimiento] = useState(() => {
    // Default: hoy + 7 días
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return toISODate(d);
  });
  const [notas, setNotas] = useState("");

  function handleClose() {
    setConcepto("");
    setMonto("");
    setNotas("");
    onOpenChange(false);
  }

  function handleSubmit() {
    const m = Number(monto);
    if (!concepto.trim()) {
      toast.error("Concepto requerido");
      return;
    }
    if (!Number.isFinite(m) || m <= 0) {
      toast.error("Monto debe ser mayor a 0");
      return;
    }
    mut.mutate(
      {
        contrato_id: contrato.id,
        monto: m.toFixed(2),
        concepto: concepto.trim(),
        fecha_vencimiento: fechaVencimiento,
        notas: notas.trim() || null,
      },
      {
        onSuccess: (factura) => {
          toast.success(`Servicio adicional ${factura.folio} emitido (${formatMXN(Number(factura.total))})`);
          handleClose();
          navigate(`/facturas/${factura.id}`);
        },
        onError: (e: any) => {
          const detail = e?.response?.data?.detail ?? e?.details?.detail ?? e?.message;
          toast.error(detail ?? "Error al emitir");
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : handleClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus size={16} />
            Emitir Servicio adicional
          </DialogTitle>
          <DialogDescription>
            Cargo extra al contrato <span className="font-mono">{contrato.codigo}</span>
            {clienteRazonSocial ? ` — ${clienteRazonSocial}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Banner explicativo */}
          <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-2.5 text-2xs text-blue-900">
            <Info size={14} className="shrink-0 mt-0.5" />
            <div>
              Este cargo <strong>no cuenta para suspensión automática</strong>. El servicio
              principal del cliente sigue activo aunque esta factura venza. Si el cliente
              paga via Toku con FIFO, esta factura podría cobrarse antes que la mensualidad
              (Toku aplica al invoice más antiguo). Útil pa cambios de domicilio, equipos
              extras, reinstalación, etc.
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium">Concepto *</label>
            <Input
              placeholder="Ej. Cambio de domicilio, Cable extra 20m, Reinstalación..."
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              autoFocus
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium">Monto (MXN) *</label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="300.00"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Vencimiento *</label>
              <Input
                type="date"
                value={fechaVencimiento}
                onChange={(e) => setFechaVencimiento(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium">Notas (opcional)</label>
            <Input
              placeholder="Detalles internos..."
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              maxLength={500}
            />
          </div>

          {/* Preview */}
          {Number(monto) > 0 && concepto.trim() && (
            <div className="rounded-md bg-muted/40 p-2.5 text-xs">
              Se emitirá una factura de <strong>{formatMXN(Number(monto))}</strong> a nombre del
              cliente, asociada al contrato <span className="font-mono">{contrato.codigo}</span>,
              con vencimiento <strong>{fechaVencimiento}</strong>. Se sincronizará con Toku para
              que el cliente pueda pagarla.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mut.isPending || !concepto.trim() || !(Number(monto) > 0)}
          >
            {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Emitir Servicio adicional
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
